<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';
import { store } from '@/app/store';
import { useLegacyCarouselPager } from '@/components/legacy-gallery/useLegacyCarouselPager';
import { useLegacyGalleryFacade } from '@/components/legacy-gallery/useLegacyGalleryFacade';
import { useLegacyGalleryTelemetry } from '@/components/legacy-gallery/useLegacyGalleryTelemetry';
// biome-ignore lint/correctness/noUnusedImports: used in <template>
import LegacyPortraitCardOverlay from './LegacyPortraitCardOverlay.vue';
import {
  // biome-ignore lint/correctness/noUnusedImports: used in <template>
  getLegacyPortraitImageSource,
  type LegacyPortraitGalleryApi,
  type LegacyPortraitGalleryItem,
} from './legacyPortraitGalleryTypes';

interface Emits {
  countDecrement: [event: MouseEvent, portrait: LegacyPortraitGalleryItem];
  countIncrement: [event: MouseEvent, portrait: LegacyPortraitGalleryItem];
  generateMore: [];
  itemClick: [event: MouseEvent, portrait: LegacyPortraitGalleryItem];
  itemLoad: [portrait: LegacyPortraitGalleryItem, index: number, event: Event];
  switchSlide: [
    delta: number,
    firstVisibleItemIndex: number,
    visibleItems: Array<LegacyPortraitGalleryItem | undefined>,
  ];
  toggleSelection: [
    event: MouseEvent,
    portrait: LegacyPortraitGalleryItem,
    isSelected: boolean,
  ];
  tryAgain: [portrait: LegacyPortraitGalleryItem];
  'update:selectedUuids': [value: string[]];
}

const CLICK_DEBOUNCE_MS = 300 as const;
const GRID_PAGE_SIZE = 4;

const props = defineProps<{
  generateMoreDisabled: boolean;
  getSelectedPortraitCount: (
    portrait: LegacyPortraitGalleryItem
  ) => number | undefined;
  items: readonly LegacyPortraitGalleryItem[];
  itemsPerSlide: number;
  itemsVersion: number;
  selectedUuids: readonly string[];
  showGenerateMore: boolean;
}>();
const emit = defineEmits<Emits>();

const imageElements = shallowRef<Array<HTMLImageElement | null>>([]);
const isVerticalKiosk = computed(() => Boolean(store.isVerticalKiosk));
const viewportIsPortrait = computed(() => Boolean(store.viewportIsPortrait));
const isGridMode = computed(() => isVerticalKiosk.value);
const actualItemsPerSlide = computed(() => {
  if (isGridMode.value) {
    return GRID_PAGE_SIZE;
  }
  return viewportIsPortrait.value || isVerticalKiosk.value
    ? 1
    : props.itemsPerSlide;
});
const gridColumnCount = computed(() => (isGridMode.value ? 2 : 1));
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const isCentered = computed(() => props.items.length < props.itemsPerSlide);
const showGenerateMoreButton = computed(
  () => props.showGenerateMore && pager.hideNextArrow.value
);

const telemetry = useLegacyGalleryTelemetry({
  actualItemsPerSlide,
  isGridMode,
  items: () => props.items,
  selectedItemUuids: () => props.selectedUuids,
  selectionMode: 'multiple',
});
const pager = useLegacyCarouselPager({
  actualItemsPerSlide,
  isGridMode,
  items: () => props.items,
  onSwitchSlide: (
    delta: number,
    firstVisibleItemIndex: number,
    visibleItems: Array<LegacyPortraitGalleryItem | undefined>
  ): void => {
    telemetry.recordSlideSwitch({
      delta,
      firstVisibleItemIndex,
      visibleItems,
    });
    emit('switchSlide', delta, firstVisibleItemIndex, visibleItems);
  },
});

function isItemSelected(portrait: LegacyPortraitGalleryItem): boolean {
  return props.selectedUuids.includes(portrait.uuid);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function isItemLoading(portrait: LegacyPortraitGalleryItem): boolean {
  return !(portrait.isPreloaded || portrait.isError);
}

function onItemClick(
  event: MouseEvent,
  portrait: LegacyPortraitGalleryItem
): void {
  if (!telemetry.recordItemClick(event, portrait)) {
    return;
  }

  emit('itemClick', event, portrait);
  if (event.defaultPrevented) {
    return;
  }

  const selected = isItemSelected(portrait);
  const updatedSelectedUuids = selected
    ? props.selectedUuids.filter((uuid) => uuid !== portrait.uuid)
    : [...props.selectedUuids, portrait.uuid];
  const isSelected = !selected;
  telemetry.recordSelection({
    isSelected,
    mode: 'multiple',
    totalSelected: updatedSelectedUuids.length,
  });
  emit('toggleSelection', event, portrait, isSelected);
  if (!event.defaultPrevented) {
    emit('update:selectedUuids', updatedSelectedUuids);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageLoad(
  portrait: LegacyPortraitGalleryItem,
  index: number,
  event: Event
): void {
  emit('itemLoad', portrait, index, event);
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onImageError(
  portrait: LegacyPortraitGalleryItem,
  index: number,
  event: Event
): void {
  if (portrait.isPreloaded !== false) {
    telemetry.recordImageLoadError(portrait, index, event);
  }
}

watch(
  () => props.itemsVersion,
  () => pager.refreshItems()
);

const {
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setContainerElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setImageElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setItemElement,
  // biome-ignore lint/correctness/noUnusedVariables: used in <template>
  setScrollableElement,
} = useLegacyGalleryFacade({
  actualItemsPerSlide,
  extras: {
    CLICK_DEBOUNCE_MS,
    canSelectItems: true,
    enableGridMode: true,
    get generateMoreDisabled(): boolean {
      return props.generateMoreDisabled;
    },
    get gridColumnCount(): number {
      return gridColumnCount.value;
    },
    get isGridMode(): boolean {
      return isGridMode.value;
    },
    get isVerticalKiosk(): boolean {
      return isVerticalKiosk.value;
    },
    itemSelection: 'multiple',
    get itemsPerSlide(): number {
      return props.itemsPerSlide;
    },
    get modelValue(): readonly string[] {
      return props.selectedUuids;
    },
    onItemClick,
    get showGenerateMoreButton(): boolean {
      return showGenerateMoreButton.value;
    },
    get viewportIsPortrait(): boolean {
      return viewportIsPortrait.value;
    },
  },
  imageElements,
  items: () => props.items,
  pager,
});

defineExpose<LegacyPortraitGalleryApi>({
  canScroll: pager.canScroll,
  isItemVisible: pager.isItemVisible,
  scrollToItem: pager.scrollToItem,
});
</script>

<template>
  <div
    :ref="setContainerElement"
    class="ImageGallery"
    :class="{
      'ImageGallery--arrow-prev-hidden': pager.hidePrevArrow.value,
      'ImageGallery--arrow-next-hidden': pager.hideNextArrow.value,
      'ImageGallery--grid-mode': isGridMode,
    }"
    data-testid="portrait-legacy-gallery"
  >
    <button
      class="ImageGallery__arrow ImageGallery__arrow--prev"
      @click="pager.switchSlide(-1)"
    />
    <button
      class="ImageGallery__arrow ImageGallery__arrow--next"
      @click="pager.switchSlide(1)"
    />
    <button
      v-if="showGenerateMoreButton"
      class="ImageGallery__generate-more"
      data-testid="gallery-generate-more"
      :disabled="generateMoreDisabled"
      @click="emit('generateMore')"
    >
      +
    </button>

    <div
      :ref="setScrollableElement"
      class="ImageGallery__scrollable hidden-scrollbar"
      :class="{ 'ImageGallery__scrollable--centered': isCentered }"
    >
      <ul class="ImageGallery__list">
        <li
          v-for="(portrait, index) in items"
          :key="pager.getItemRenderKey(portrait, index)"
          :ref="(element) => setItemElement(element, index)"
          class="ImageGallery__item"
          :class="{
            'ImageGallery__item--selected': isItemSelected(portrait),
            'Portraits__item--loading': isItemLoading(portrait),
            'LegacyPortraitGallery__item--loading': isItemLoading(portrait),
          }"
          data-testid="gallery-item"
          :data-selected="isItemSelected(portrait) ? 'true' : 'false'"
          :data-loading="
            portrait.isPreloaded === false && !portrait.isError
              ? 'true'
              : 'false'
          "
          :data-error="portrait.isError ? 'true' : 'false'"
          @click="onItemClick($event, portrait)"
        >
          <div
            class="ImageGallery__item-wrapper"
            :class="{
              'LegacyPortraitGallery__item-wrapper--error': portrait.isError,
            }"
            :style="
              portrait.aspectRatio
                ? { '--ImageGallery--item-ratio': portrait.aspectRatio }
                : undefined
            "
          >
            <div class="ImageGallery__item-overlay">
              <LegacyPortraitCardOverlay
                :portrait="portrait"
                :selected="isItemSelected(portrait)"
                :selected-count="getSelectedPortraitCount(portrait)"
                @try-again="emit('tryAgain', $event)"
                @count-decrement="
                  (event, item) => emit('countDecrement', event, item)
                "
                @count-increment="
                  (event, item) => emit('countIncrement', event, item)
                "
              />
            </div>

            <img
              :ref="(element) => setImageElement(element, index)"
              class="ImageGallery__item-image LegacyPortraitGallery__image sentry-block"
              :class="{
                'LegacyPortraitGallery__image--preloaded':
                  portrait.isPreloaded,
              }"
              :src="getLegacyPortraitImageSource(portrait)"
              @load="onImageLoad(portrait, index, $event)"
              @error="onImageError(portrait, index, $event)"
            />

            <i class="ImageGallery__item-checkmark kiosk-success-icon" />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.LegacyPortraitGallery__item--loading {
  pointer-events: none;
}

.LegacyPortraitGallery__item-wrapper--error {
  cursor: default;
}

.LegacyPortraitGallery__image {
  opacity: 0;

  &--preloaded {
    opacity: 1;
  }
}
</style>
